// Comprehensive Zakat Calculation Service
import type {
  ZakatType,
  CalendarType,
  IrrigationType,
  ZakatMoneyInput,
  ZakatGoldInput,
  ZakatSilverInput,
  ZakatCropsInput,
  ZakatLivestockInput,
  ZakatFreelanceInput,
  ZakatCalculationResult,
} from '@/types/zakat'

const ZAKAT_PERCENTAGE = 2.5 // 2.5%

// Nisab values
const NISAB_GOLD_GRAMS = 85 // 85 grams of 24-carat gold
const NISAB_SILVER_GRAMS = 595 // 595 grams of silver

/**
 * Calculate Zakat on Money (زكاة الأموال)
 * النصاب = سعر جرام الذهب عيار 24 × 85 جرام
 */
export function calculateZakatMoney(
  input: ZakatMoneyInput,
  calendar: CalendarType
): ZakatCalculationResult {
  // Calculate nisab: 85 grams of 24-carat gold
  const nisab = input.goldPricePerGram24 > 0 
    ? input.goldPricePerGram24 * NISAB_GOLD_GRAMS 
    : 0
  
  // If nisab is 0 (gold price not entered), return early
  if (nisab === 0) {
    return {
      nisab: 0,
      nisabUnit: 'currency',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: input.amount,
    }
  }
  
  // Check if amount reaches nisab threshold
  if (input.amount < nisab) {
    return {
      nisab,
      nisabUnit: 'currency',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: input.amount,
    }
  }

  // Calculate zakat: 2.5% of total amount
  const zakatAmount = (input.amount * ZAKAT_PERCENTAGE) / 100

  return {
    nisab,
    nisabUnit: 'currency',
    zakatAmount,
    isZakatDue: true,
    totalAmount: input.amount,
  }
}

/**
 * Calculate Zakat on Gold (زكاة الذهب)
 * النصاب: 85 جرام من الذهب عيار 24
 * الزكاة: 2.5% من قيمة الذهب
 */
export function calculateZakatGold(
  input: ZakatGoldInput,
  calendar: CalendarType
): ZakatCalculationResult {
  // Convert all to 24-carat equivalent by purity (not by price)
  // عيار 18 = 18/24 = 0.75 من عيار 24
  // عيار 21 = 21/24 = 0.875 من عيار 24
  // عيار 24 = 1.0
  const gold24Equivalent =
    (input.grams18 * 18 / 24) +
    (input.grams21 * 21 / 24) +
    input.grams24

  // Calculate total value in currency
  const totalValue = 
    (input.grams18 * input.price18) +
    (input.grams21 * input.price21) +
    (input.grams24 * input.price24)

  const nisabInGrams = NISAB_GOLD_GRAMS

  // Check if gold reaches nisab (85 grams of 24-carat equivalent)
  if (gold24Equivalent < NISAB_GOLD_GRAMS) {
    return {
      nisab: nisabInGrams,
      nisabUnit: 'gram',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: totalValue,
    }
  }

  // Calculate zakat: 2.5% of total gold value
  const zakatAmount = (totalValue * ZAKAT_PERCENTAGE) / 100

  return {
    nisab: nisabInGrams,
    nisabUnit: 'gram',
    zakatAmount,
    isZakatDue: true,
    totalAmount: totalValue,
  }
}

/**
 * Calculate Zakat on Silver (زكاة الفضة)
 */
export function calculateZakatSilver(
  input: ZakatSilverInput,
  calendar: CalendarType
): ZakatCalculationResult {
  const totalValue = input.grams * input.pricePerGram
  const nisabInGrams = NISAB_SILVER_GRAMS

  if (input.grams < NISAB_SILVER_GRAMS) {
    return {
      nisab: nisabInGrams,
      nisabUnit: 'gram',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: totalValue,
    }
  }

  const zakatAmount = (totalValue * ZAKAT_PERCENTAGE) / 100

  return {
    nisab: nisabInGrams,
    nisabUnit: 'gram',
    zakatAmount,
    isZakatDue: true,
    totalAmount: totalValue,
  }
}

/**
 * Calculate Zakat on Crops (زكاة الزروع والثمار)
 */
export function calculateZakatCrops(
  input: ZakatCropsInput,
  calendar: CalendarType
): ZakatCalculationResult {
  const totalValue = input.kilograms * input.pricePerKilogram
  
  // Nisab for crops is 5 wasaq = 647 kg
  const NISAB_CROPS_KG = 647
  const nisabInKg = NISAB_CROPS_KG

  if (input.kilograms < NISAB_CROPS_KG) {
    return {
      nisab: nisabInKg,
      nisabUnit: 'kilogram',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: totalValue,
    }
  }

  // Zakat percentage depends on irrigation type
  let zakatPercentage: number
  switch (input.irrigationType) {
    case 'withoutCost': // تسقي بدون تكلفة (rain or natural)
      zakatPercentage = 10
      break
    case 'withCost': // تسقي بتكلفة (irrigated with cost)
      zakatPercentage = 5
      break
    case 'sharedCost': // تسقي بالمناصفة (shared cost)
      zakatPercentage = 7.5
      break
    default:
      zakatPercentage = 5
  }

  const zakatAmount = (totalValue * zakatPercentage) / 100

  return {
    nisab: nisabInKg,
    nisabUnit: 'kilogram',
    zakatAmount,
    isZakatDue: true,
    totalAmount: totalValue,
  }
}

/**
 * Calculate Zakat on Livestock (زكاة الأنعام)
 * زكاة الأنعام تعتمد على جداول مفصلة حسب نوع وعدد الحيوانات
 */
export function calculateZakatLivestock(
  input: ZakatLivestockInput,
  calendar: CalendarType
): ZakatCalculationResult {
  // Minimum nisab for livestock (varies by type)
  // For sheep/goats: 40
  // For camels: 5
  // For cattle: 30
  
  const hasNisab = 
    input.sheep >= 40 || 
    input.camels >= 5 || 
    input.cattle >= 30

  // Calculate zakat based on detailed tables for each type separately
  const zakatDescriptions: string[] = []
  let zakatValue = 0

  // Camels (الإبل)
  if (input.camels >= 5) {
    if (input.camels >= 5 && input.camels <= 9) {
      zakatDescriptions.push('1 شاة (للإبل)')
      zakatValue = 1
    } else if (input.camels >= 10 && input.camels <= 14) {
      zakatDescriptions.push('2 شاة (للإبل)')
      zakatValue = 2
    } else if (input.camels >= 15 && input.camels <= 19) {
      zakatDescriptions.push('3 شاة (للإبل)')
      zakatValue = 3
    } else if (input.camels >= 20 && input.camels <= 24) {
      zakatDescriptions.push('4 شاة (للإبل)')
      zakatValue = 4
    } else if (input.camels >= 25 && input.camels <= 35) {
      zakatDescriptions.push('1 بنت مخاض (للإبل)')
      zakatValue = 1
    } else if (input.camels >= 36 && input.camels <= 45) {
      zakatDescriptions.push('1 بنت لبون (للإبل)')
      zakatValue = 1
    } else if (input.camels >= 46 && input.camels <= 60) {
      zakatDescriptions.push('1 حِقة (للإبل)')
      zakatValue = 1
    } else if (input.camels >= 61 && input.camels <= 75) {
      zakatDescriptions.push('1 جذعة (للإبل)')
      zakatValue = 1
    } else if (input.camels >= 76 && input.camels <= 90) {
      zakatDescriptions.push('2 بنت لبون (للإبل)')
      zakatValue = 2
    } else if (input.camels >= 91 && input.camels <= 120) {
      zakatDescriptions.push('2 حِقة (للإبل)')
      zakatValue = 2
    } else if (input.camels > 120) {
      // بنت لبون لكل 40 أو حِقة لكل 50
      const by40 = Math.floor((input.camels - 120) / 40)
      const by50 = Math.floor((input.camels - 120) / 50)
      zakatDescriptions.push(`2 حِقة + ${by40 > by50 ? by40 + ' بنت لبون' : by50 + ' حِقة'} (للإبل)`)
      zakatValue = 2 + Math.max(by40, by50)
    }
  }

  // Sheep/Goats (الغنم)
  if (input.sheep >= 40) {
    if (input.sheep >= 40 && input.sheep <= 120) {
      zakatDescriptions.push('1 شاة (للغنم)')
      zakatValue += 1
    } else if (input.sheep >= 121 && input.sheep <= 200) {
      zakatDescriptions.push('2 شاة (للغنم)')
      zakatValue += 2
    } else if (input.sheep >= 201 && input.sheep <= 399) {
      zakatDescriptions.push('3 شاة (للغنم)')
      zakatValue += 3
    } else if (input.sheep >= 400) {
      const sheepZakat = Math.floor(input.sheep / 100)
      zakatDescriptions.push(`${sheepZakat} شاة (للغنم)`)
      zakatValue += sheepZakat
    }
  }

  // Cattle (البقر)
  if (input.cattle >= 30) {
    if (input.cattle >= 30 && input.cattle <= 39) {
      zakatDescriptions.push('1 بقرة عمر سنة (للأبقار)')
      zakatValue += 1
    } else if (input.cattle === 40) {
      zakatDescriptions.push('1 بقرة عمر سنتين (للأبقار)')
      zakatValue += 1
    } else if (input.cattle > 40) {
      const by30 = Math.floor((input.cattle - 40) / 30)
      const by40 = Math.floor((input.cattle - 40) / 40)
      zakatDescriptions.push(
        `1 بقرة عمر سنتين + ${by30 > by40 ? by30 + ' بقرة عمر سنة' : by40 + ' بقرة عمر سنتين'} (للأبقار)`
      )
      zakatValue += 1 + Math.max(by30, by40)
    }
  }

  const zakatDescription = zakatDescriptions.join(' + ')

  if (!hasNisab) {
    return {
      nisab: 0,
      nisabUnit: 'animals',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: input.sheep + input.camels + input.cattle,
    }
  }

  // Note: زكاة الأنعام تُعطى كحيوانات وليس مال
  // هنا نعرض الوصف فقط
  return {
    nisab: 0,
    nisabUnit: 'animals',
    zakatAmount: zakatValue, // عدد الحيوانات المستحقة
    isZakatDue: true,
    totalAmount: input.sheep + input.camels + input.cattle,
    zakatDescription, // وصف الزكاة المستحقة
  }
}

/**
 * Calculate Zakat on Freelance Professions (زكاة المهن الحرة)
 */
export function calculateZakatFreelance(
  input: ZakatFreelanceInput,
  calendar: CalendarType
): ZakatCalculationResult {
  // Net income = Revenues - Wages and Operating Expenses - Living Expenses - Debts
  // حسب الموقع المرجعي: الإيرادات - الأجور ومصاريف التشغيل - نفقات المعيشة - الديون
  const netIncome = 
    input.revenues - 
    input.wagesAndExpenses - 
    input.livingExpenses - 
    input.debts

  if (netIncome <= 0) {
    return {
      nisab: input.goldPricePerGram24 * NISAB_GOLD_GRAMS,
      nisabUnit: 'currency',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: netIncome,
    }
  }

  const nisab = input.goldPricePerGram24 * NISAB_GOLD_GRAMS

  if (netIncome < nisab) {
    return {
      nisab,
      nisabUnit: 'currency',
      zakatAmount: 0,
      isZakatDue: false,
      totalAmount: netIncome,
    }
  }

  const zakatAmount = (netIncome * ZAKAT_PERCENTAGE) / 100

  return {
    nisab,
    nisabUnit: 'currency',
    zakatAmount,
    isZakatDue: true,
    totalAmount: netIncome,
  }
}

/**
 * Main calculation function
 */
export function calculateZakat(input: {
  type: ZakatType
  data: any
  calendar: CalendarType
}): ZakatCalculationResult {
  switch (input.type) {
    case 'money':
      return calculateZakatMoney(input.data as ZakatMoneyInput, input.calendar)
    case 'gold':
      return calculateZakatGold(input.data as ZakatGoldInput, input.calendar)
    case 'silver':
      return calculateZakatSilver(input.data as ZakatSilverInput, input.calendar)
    case 'crops':
      return calculateZakatCrops(input.data as ZakatCropsInput, input.calendar)
    case 'livestock':
      return calculateZakatLivestock(input.data as ZakatLivestockInput, input.calendar)
    case 'freelance':
      return calculateZakatFreelance(input.data as ZakatFreelanceInput, input.calendar)
    default:
      throw new Error(`Unknown Zakat type: ${input.type}`)
  }
}

